import React from 'react';
import * as R from 'ramda';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose, withState, withHandlers } from 'react-recompose';
// features
import Audit2 from '../audit2';
import PC from '../permission/role-permission';
// forms
import { SelectDropdownForm } from '../../forms/forms/select-dropdown-form';
import { SelectGLPostedDateForm } from '../../forms/forms/select-gl-posted-date-form';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// feature master-invoice
import { RowActions } from './components/actions';
import DocumentFormWithAsyncCloEvents from './components/document-form-with-async-clo-events';
import { printCloInvoiceRequest, updateCloInvoiceRequest, createCloDocumentRequest } from './actions';
import {
  makeSelectUpdatedInvoice,
  makeSelectDocumentTypeOptions,
  makeSelectMasterInvoiceConfigs,
} from './selectors';
//////////////////////////////////////////////////

const mapStateToProps = (state) => createStructuredSelector({
  configs: makeSelectMasterInvoiceConfigs(state),
  updatedInvoice: makeSelectUpdatedInvoice(state),
  documentTypeOptions: makeSelectDocumentTypeOptions(state),
});

const addCloDocumentAndPrintCloInvoiceActions = {
  printCloInvoiceRequest,
  updateCloInvoiceRequest,
  createCloDocumentRequest,
};

const withAddCloDocumentAndPrintCloInvoice = compose(
  connect(mapStateToProps, addCloDocumentAndPrintCloInvoiceActions),
  withState('documentTypes', 'setDocumentTypes', (props) => R.compose(
    R.filter(G.isNotNilAndNotEmpty),
    R.map((value) => R.find(R.propEq(value, GC.FIELD_VALUE), props.documentTypeOptions)),
    R.pathOr([], ['configs', 'defaultDocumentTypeGuids']),
  )(props)),
  withHandlers({
    handleAddCloDocument: (props) => (invoice, callback) => {
      const {
        openModal,
        closeFixedPopup,
        createCloDocumentRequest,
      } = props;

      const cloGuid = R.prop(GC.FIELD_CLO_GUID, invoice);

      const handler = (options) => {
        const valuesToUse = G.mapObjectEmptyStringFieldsToNull(options.data);

        createCloDocumentRequest({
          callback,
          setSubmitting: options.callback,
          reqData: R.assoc(GC.FIELD_PRIMARY_OBJECT_GUID, cloGuid, valuesToUse),
        });
      };

      const component = (
        <DocumentFormWithAsyncCloEvents
          {...props}
          cloGuid={cloGuid}
          load={{ loadType: GC.FIELD_CLO }}
          handleActionLoadDocument={handler}
          initialValues={{ [GC.FIELD_DOCUMENT_PROOF_TYPE]: 'NONE' }}
        />
      );

      const modal = {
        p: 15,
        component,
        options: {
          width: 'min-content',
          height: 'max-content',
          title: G.getWindowLocale('titles:add-doc', 'Add Document'),
        },
      };

      closeFixedPopup();
      openModal(modal);
    },
  }),
);

const withHandleAudit = withHandlers({
  handleShowAudit: (props) => ({ guid, masterInvoiceNumber }) => {
    const { openModal, closeFixedPopup } = props;

    closeFixedPopup();

    const requestOptions = {
      [GC.FIELD_OBJECT_GUID]: guid,
      [GC.FIELD_TYPE]: GC.AUDIT_TYPE_MASTER_INVOICE,
    };

    const component = <Audit2 requestOptions={requestOptions} />;

    const modal = {
      p: '0',
      component,
      options: {
        withCloseIcon: true,
        backgroundColor: 'transparent',
        title: `${G.getWindowLocale('titles:audit', 'Audit')}: ${masterInvoiceNumber}`,
      },
    };

    openModal(modal);
  },
});

const withMasterInvoiceActions = compose(
  withHandlers({
    handleGenerateFactoringFileByFactoringType: (props) => (data) => {
      const { availableFactoringTypes, getFactoringCompanyFileRequest } = props;

      const { factoringType } = data;

      if (R.pathEq(factoringType, [GC.FIELD_FACTORING_TYPE], availableFactoringTypes)) {
        return getFactoringCompanyFileRequest(data);
      }

      const messagesMap = {
        [GC.FACTORING_COMPANY_TYPE_TRIUMPH]: ['messages:check-triumph-configs', 'Please, check your Triumph configs'],
        [GC.FACTORING_COMPANY_TYPE_COMPASS]: ['messages:check-compass-configs', 'Please, check your Compass configs'],
        [GC.FACTORING_COMPANY_TYPE_RTS_FINANCIAL]: [
          'messages:check-rts-financial-configs',
          'Please, check your RTS Financial configs',
        ],
        [GC.FACTORING_COMPANY_TYPE_OTR_CAPITAL]: [
          'messages:check-otr-capital-configs',
          'Please, check your OTR Capital configs',
        ],
        [GC.FACTORING_COMPANY_TYPE_RTS_FINANCIAL_MANUAL]: [
          'messages:check-rts-financial-manual-configs',
          'Please, check your RTS Financial Manual configs',
        ],
      };

      const message = G.getWindowLocale(...R.propOr([], factoringType, messagesMap));

      return G.showToastrMessageSimple('info', message);
    },
    handleSelectOptionForExport: (props) => (data, actionID) => {
      const { openModal, closeModal, qbIIFImportRequest, sendToQuickBookRequest, sendToSageIntacctRequest } = props;

      const actionsMap = {
        IFF: qbIIFImportRequest,
        QB: sendToQuickBookRequest,
        SAGE: sendToSageIntacctRequest,
      };

      const submitAction = (value) => {
        const action = actionsMap[actionID];

        const payload = {
          ...data,
          separateInvoices: R.equals(value, 'AS_SINGLE'),
        };

        action(payload);

        closeModal();
      };

      const options = [
        { label: G.getWindowLocale('titles:as-single', 'As Single'), value: 'AS_SINGLE' },
        { label: G.getWindowLocale('titles:as-master', 'As Master'), value: 'AS_MASTER' },
      ];

      const component = (
        <SelectDropdownForm
          options={options}
          optionRequired={false}
          cancelAction={closeModal}
          submitAction={submitAction}
          initialValues={{ option: 'AS_MASTER' }}
          fieldLabel={G.getWindowLocale('titles:select-export-option', 'Select Export Option')}
        />
      );

      const modal = { p: 15, component, options: {} };

      openModal(modal);
    },
    handleExportToQuickBooksDesktop: (props) => (data) => {
      const { openModal, closeModal, sendToQuickBookRequest } = props;

      const options = [
        { label: G.getWindowLocale('titles:as-single', 'As Single'), value: 'AS_SINGLE' },
      ];

      const additionFields = G.hasAmousCurrentUserPermissions(PC.RESEND_INVOICES_TO_ACCOUNTING_EXECUTE) ? [
        {
          type: 'checkbox',
          fieldName: 'resendExported',
          inputWrapperStyles: { mb: 25 },
          label: ['messages:include-already-exported-invoices', 'Include already exported invoices?'],
        },
      ] : [];

      const submitAction = ({ option, resendExported }) => {
        const payload = {
          ...data,
          desktop: true,
          resendExported,
          separateInvoices: R.equals(option, 'AS_SINGLE'),
        };

        sendToQuickBookRequest(payload);

        closeModal();
      };

      const component = (
        <SelectDropdownForm
          options={options}
          submitValues={true}
          optionRequired={false}
          cancelAction={closeModal}
          submitAction={submitAction}
          additionFields={additionFields}
          fieldsWrapperStyles={{ width: 300 }}
          initialValues={{ option: 'AS_SINGLE' }}
          fieldLabel={G.getWindowLocale('titles:select-export-option', 'Select Export Option')}
        />
      );

      const modal = { p: 15, component, options: {} };

      openModal(modal);
    },
    handleExportCustomerMasterInvoiceToEDI: (props) => (guid) => {
      const { closeFixedPopup, exportCustomerMasterInvoiceToEDIRequest } = props;

      closeFixedPopup();
      exportCustomerMasterInvoiceToEDIRequest(guid);
    },
  }),
  withHandlers({
    handleSendMasterInvoiceListToSageIntacct: (props) => (guid) => {
      const {
        itemList,
        openModal,
        closeModal,
        handleSelectOptionForExport,
      } = props;

      const guids = G.getGuidsFromStringOrSelectedList(guid, itemList);

      if (R.propEq(0, 'length', guids)) {
        return G.showToastrMessageSimple(
          'info',
          G.getWindowLocale('messages:driver:select-invoice', 'Please, select an Invoice!'),
        );
      }

      const component = (
        <SelectGLPostedDateForm
          closeModal={closeModal}
          submitAction={(date) => {
            closeModal();
            handleSelectOptionForExport({
              guids,
              [GC.FIELD_GL_POSTED_DATE]: date,
            }, 'SAGE');
          }}
        />
      );

      const modal = {
        p: 15,
        component,
        options: {
          width: 300,
          height: 'auto',
          footerZIndex: 'unset',
          title: G.getWindowLocale('titles:gl-posted-date', 'GL Posted Date'),
        },
      };

      openModal(modal);
    },
    handleCustomExport: (props) => (guid) => {
      const { closeFixedPopup, handleCustomExport } = props;

      handleCustomExport(guid);
      G.callFunction(closeFixedPopup);
    },
  }),
  withHandlers({
    handleClickEditIcon: (props) => (e, masterInvoice) => {
      const {
        configs,
        openFixedPopup,
        closeFixedPopup,
        handleShowAudit,
        handleCustomExport,
        handlePrintInvoices,
        handleShowDocuments,
        availableFactoringTypes,
        handlePrintMasterInvoice,
        getMasterInvoiceXMLRequest,
        handleSelectOptionForExport,
        availableCustomExportOptions,
        handleOpenSubmitDeleteMasterInvoice,
        handleOpenUpdateMasterInvoiceInvoices,
        handleExportCustomerMasterInvoiceToEDI,
        handleSendMasterInvoiceListToSageIntacct,
        handleGenerateFactoringFileByFactoringType,
      } = props;

      const dataToSend = {
        guids: R.of(Array, G.getGuidFromObject(masterInvoice)),
      };

      openFixedPopup({
        position: 'right',
        el: e.currentTarget,
        boxStyles: { zIndex: 16 },
        content: (
          <RowActions
            configs={configs}
            data={masterInvoice}
            closeFixedPopup={closeFixedPopup}
            availableFactoringTypes={availableFactoringTypes}
            handleShowAudit={() => handleShowAudit(masterInvoice)}
            handlePrintCloInvoices={() => handlePrintInvoices(masterInvoice)}
            sendToSageIntacctRequest={handleSendMasterInvoiceListToSageIntacct}
            handlePrintMasterInvoice={() => handlePrintMasterInvoice(masterInvoice)}
            handleGetXMLRequest={(data) => getMasterInvoiceXMLRequest(data)}
            qbIIFImportRequest={() => handleSelectOptionForExport(dataToSend, 'IFF')}
            availableCustomExport={G.isNotNilAndNotEmpty(availableCustomExportOptions)}
            openUpdateInvoices={() => handleOpenUpdateMasterInvoiceInvoices(masterInvoice)}
            handleCustomExport={() => handleCustomExport(G.getGuidFromObject(masterInvoice))}
            handleSendToQuickBookRequest={() => handleSelectOptionForExport(dataToSend, 'QB')}
            handleDeleteMasterInvoice={() => handleOpenSubmitDeleteMasterInvoice(masterInvoice)}
            handleGenerateFactoringFileByFactoringType={handleGenerateFactoringFileByFactoringType}
            handleExportCustomerMasterInvoiceToEDI={() =>
              handleExportCustomerMasterInvoiceToEDI(G.getGuidFromObject(masterInvoice))
            }
            showDocuments={() =>
              handleShowDocuments(G.getGuidFromObject(masterInvoice), GC.DOCUMENTS_TYPE_MASTER_INVOICE)}
          />
        ),
      });
    },
  }),
);

export {
  withHandleAudit,
  withMasterInvoiceActions,
  withAddCloDocumentAndPrintCloInvoice,
};
